/**
 * 教程数据定义
 * @description 5 个关卡的教学内容配置
 */

export interface TutorialData {
  /** 关卡标题 */
  title: string;
  /** 物理公式 */
  formula: string;
  /** 变量解释列表 */
  variables?: string[];
  /** 关卡目标 */
  goal: string;
  /** 操作提示 */
  hint: string;
}

export const TUTORIALS: Record<string, TutorialData> = {
  'level-1': {
    title: '速度与位移',
    formula: 'v = s / t',
    variables: [
      'v = 速度（单位/秒）',
      's = 距离（单位）',
      't = 时间（秒）',
    ],
    goal: '计算正确速度，使火柴人在指定时间内到达终点',
    hint: '距离 s=400，目标时间 t=5秒，计算 v=?',
  },

  'level-2': {
    title: '加速度入门',
    formula: 'a = Δv / Δt',
    variables: [
      'a = 加速度（单位/秒²）',
      'Δv = 速度变化量',
      'Δt = 时间变化',
      'v = v₀ + a × t （速度公式）',
    ],
    goal: '设置初速度和加速度，使火柴人以目标速度到达终点',
    hint: '观察斜坡上的速度变化',
  },

  'level-3': {
    title: '抛物线运动',
    formula: 'y = v₀·sin(θ)·t - ½gt²',
    variables: [
      'v₀ = 初速度',
      'θ = 发射角度',
      't = 时间',
      'g = 重力加速度（≈10）',
    ],
    goal: '设置初速度和发射角度，让火柴人落入目标区域',
    hint: '调整角度和速度，预判抛物线轨迹',
  },

  'level-4': {
    title: '摩擦力探索',
    formula: 'f = μ · N',
    variables: [
      'f = 摩擦力',
      'μ = 摩擦系数',
      'N = 正压力',
      '冰面 μ=0.1（极低）',
      '木地板 μ=0.4（中等）',
      '砂纸 μ=0.8（高）',
    ],
    goal: '探索不同材质地面的摩擦效果',
    hint: '摩擦系数越小，滑动距离越长',
  },

  'level-5': {
    title: '综合挑战',
    formula: '综合运用所学公式',
    variables: [
      '阶段1：速度 v = s/t',
      '阶段2：加速度 a = F/m',
      '阶段3：抛物线运动',
      '阶段4：摩擦力 f = μN',
    ],
    goal: '完成四阶段终极挑战',
    hint: '每个阶段需要不同的策略',
  },

  'level-6': {
    title: '电场探索',
    formula: 'F = qE',
    variables: [
      'F = 电场力',
      'q = 电荷量',
      'E = 电场强度',
      'E = kQ/r²（点电荷场）',
    ],
    goal: '理解电场对电荷的作用力',
    hint: '正电荷受力方向与电场方向相同',
  },

  'level-7': {
    title: '磁场与洛伦兹力',
    formula: 'F = qv×B',
    variables: [
      'F = 洛伦兹力（磁场力）',
      'q = 电荷量',
      'v = 粒子速度',
      'B = 磁场强度',
      '力方向垂直于速度和磁场',
      '粒子做圆周运动',
    ],
    goal: '理解磁场对运动电荷的偏转作用',
    hint: '洛伦兹力只改变速度方向，不改变速度大小',
  },

  'level-8': {
    title: '电磁感应',
    formula: 'ε = -dΦ/dt = -B·L·v',
    variables: [
      'ε = 感应电动势（伏特）',
      'Φ = 磁通量（韦伯）',
      'B = 磁场强度（特斯拉）',
      'L = 导体长度（米）',
      'v = 切割速度（米/秒）',
      '负号表示楞次定律',
    ],
    goal: '移动导体穿越磁场，产生足够的感应电压',
    hint: '导体速度越快，感应电压越大！拖拽导体穿过紫色磁场区域',
  },

  'level-9': {
    title: '安培定律',
    formula: 'B = μ₀I / 2πr',
    variables: [
      'B = 磁场强度（特斯拉）',
      'μ₀ = 真空磁导率（4π×10⁻⁷）',
      'I = 电流强度（安培）',
      'r = 距离导线的距离（米）',
      '右手定则：拇指指向电流方向',
      '四指弯曲方向为磁场方向',
    ],
    goal: '调节电流强度，使磁场达到目标值',
    hint: '电流越大，磁场越强。观察罗盘方向变化！',
  },

  'level-10': {
    title: '电磁综合挑战',
    formula: '综合运用电磁学定律',
    variables: [
      '库仑定律：F = kQ₁Q₂/r²',
      '电场强度：E = kQ/r²',
      '洛伦兹力：F = qv×B',
      '电磁感应：ε = -dΦ/dt',
      '安培定律：B = μ₀I/2πr',
    ],
    goal: '完成电磁学终极挑战',
    hint: '综合运用电场、磁场和电磁感应知识！',
  },

  // Phase 3: 光与相对论（11-15关）
  'level-11': {
    title: '光速限制',
    formula: 'v < c',
    variables: [
      'c = 光速（299,792,458 m/s）',
      'v = 物体速度',
      '任何有质量物体无法达到光速',
      '相对论速度叠加：v = (v₁ + v₂)/(1 + v₁v₂/c²)',
    ],
    goal: '理解光速是宇宙的极限速度',
    hint: '即使两个高速物体相向而行，合速度也不会超过光速！',
  },

  'level-12': {
    title: '时间膨胀',
    formula: 't\' = γt',
    variables: [
      't\' = 运动参考系中的时间',
      't = 静止参考系中的时间',
      'γ = 洛伦兹因子 = 1/√(1 - v²/c²)',
      '高速运动时时间变慢',
    ],
    goal: '理解高速运动时时间流逝变慢',
    hint: '速度越快，时间膨胀效应越明显！',
  },

  'level-13': {
    title: '长度收缩',
    formula: 'L\' = L/γ',
    variables: [
      'L\' = 运动参考系中测量的长度',
      'L = 静止参考系中的长度（本征长度）',
      'γ = 洛伦兹因子 = 1/√(1 - v²/c²)',
      '只在运动方向上收缩',
      '垂直方向不受影响',
    ],
    goal: '理解高速运动时物体在运动方向上缩短',
    hint: '选择足够高的速度，让物体收缩后能通过门洞！',
  },

  'level-14': {
    title: '红移蓝移',
    formula: 'D = √((1 ± β)/(1 - β))',
    variables: [
      'D = 多普勒因子',
      'β = v/c（速度比率）',
      '接近时蓝移：频率增加',
      '远离时红移：频率降低',
      '天文观测的重要工具',
    ],
    goal: '理解相对论多普勒效应',
    hint: '观察光源的颜色变化！',
  },

  'level-15': {
    title: '相对论综合挑战',
    formula: '综合运用相对论知识',
    variables: [
      '光速限制：v < c',
      '时间膨胀：t\' = γt',
      '长度收缩：L\' = L/γ',
      '多普勒效应：红移/蓝移',
      '洛伦兹因子：γ = 1/√(1 - v²/c²)',
    ],
    goal: '完成相对论终极挑战',
    hint: '综合运用所有相对论效应！',
  },

  // Phase 4: 黑洞宇宙（16-20关）
  'level-16': {
    title: '黑洞引力',
    formula: 'Rs = 2GM/c²',
    variables: [
      'Rs = 史瓦西半径（事件视界）',
      'G = 万有引力常数',
      'M = 黑洞质量',
      'c = 光速',
      '逃逸速度 v = √(2GM/r)',
    ],
    goal: '理解黑洞的引力效应和事件视界',
    hint: '任何物质（包括光）一旦进入事件视界就无法逃逸！',
  },

  'level-17': {
    title: '引力透镜',
    formula: 'δ = 2Rs/r',
    variables: [
      'δ = 光线偏折角',
      'Rs = 史瓦西半径',
      'r = 光线到黑洞的最近距离',
      '爱因斯坦环：r_E = √(4GMd/c²)',
      '光线经过黑洞时被弯曲',
    ],
    goal: '理解引力透镜效应，调整光线方向击中目标',
    hint: '光源、黑洞、观测者对齐时会形成爱因斯坦环！',
  },

  'level-18': {
    title: '虫洞穿越',
    formula: '时空捷径',
    variables: [
      '虫洞：连接两个遥远时空点的通道',
      '爱因斯坦-罗森桥：理论预言的结构',
      '穿越虫洞可实现瞬间空间转移',
      '需要奇异物质维持虫洞稳定',
      '虫洞穿越可能实现时间旅行',
    ],
    goal: '利用虫洞实现空间跳跃，到达目标位置',
    hint: '虫洞是时空的捷径，可瞬间穿越遥远距离！',
  },

  'level-19': {
    title: '黑洞视界',
    formula: 'Rs = 2GM/c²',
    variables: [
      'Rs = 史瓦西半径（事件视界）',
      'G = 万有引力常数',
      'M = 黑洞质量',
      'c = 光速',
      '引力红移：z = 1/√(1-Rs/r) - 1',
      '事件视界内：时间无限膨胀',
    ],
    goal: '在视界边缘收集数据并安全返回',
    hint: '越接近视界，时间越慢，红移越大！切勿越过视界！',
  },

  'level-20': {
    title: '宇宙创生',
    formula: '大爆炸理论',
    variables: [
      '宇宙起源于约138亿年前的大爆炸',
      '时空从奇点开始膨胀',
      '早期宇宙高温高密度',
      '宇宙微波背景辐射：大爆炸余晖',
      '暗能量驱动宇宙加速膨胀',
    ],
    goal: '见证宇宙创生的壮丽景象',
    hint: '探索宇宙起源的终极奥秘！',
  },
};

export function getTutorial(levelId: string): TutorialData | undefined {
  return TUTORIALS[levelId];
}